import { useCallback, useEffect, useRef, useState } from "react";

import { styled } from "@linaria/react";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import FlexWrapper, { AlignItems, FlexDirection, JustifyContent } from "../containers/FlexWrapper";
import Text from "../text/Text";
import { InputLabel } from "./Input";

export interface SliderInputProps {
  label?: string;
  labelTooltip?: string | React.ReactNode;
  value: number;
  onChange: (value: number) => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
  min?: number;
  max?: number;
  step?: number;
  isDisabled?: boolean;
  width?: number | string;
  fillWidth?: boolean;
}

const SliderWrapper = withTheme(styled.div<
  PropsWithTheme<{
    $isDisabled: boolean;
  }>
>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "pointer")};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
`);

const SliderTrack = withTheme(styled.div<
  PropsWithTheme<{
    $isDisabled: boolean;
  }>
>`
  position: relative;
  width: 100%;
  height: 3px;
  background: ${({ theme }) => theme.color.opacity.bw24Alt};
  border-radius: 200px;
`);

const SliderFill = withTheme(styled.div<
  PropsWithTheme<{
    $percentage: number;
    $isDisabled: boolean;
  }>
>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: ${({ theme }) => theme.color.icon.primary};
  border-radius: 200px;
  z-index: 1;
`);

const SliderHandle = withTheme(styled.div<
  PropsWithTheme<{
    $percentage: number;
    $isDisabled: boolean;
    $isDragging: boolean;
  }>
>`
  position: absolute;
  left: ${({ $percentage }) => `calc(4px + ${$percentage}% - ${($percentage / 100) * 8}px)`};
  top: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: ${({ theme }) => theme.color.icon.primary};
  border-radius: 50%;
  cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "grab")};
  transition: ${({ $isDragging }) =>
    $isDragging ? "none" : "left 0.1s ease-out, transform 0.1s ease-out"};
  z-index: 3;
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? "none" : "auto")};

  &:active {
    cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "grabbing")};
    transform: translate(-50%, -50%) scale(1.1);
  }

  &:hover {
    transform: ${({ $isDisabled }) =>
      $isDisabled ? "translate(-50%, -50%)" : "translate(-50%, -50%) scale(1.1)"};
  }
`);

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  cursor: pointer;
  z-index: 2;
`;

const SliderInput = ({
  label,
  labelTooltip,
  value,
  onChange,
  onInteractionStart,
  onInteractionEnd,
  min = 0,
  max = 100,
  step = 1,
  isDisabled = false,
  width,
  fillWidth,
}: SliderInputProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const isInteractingRef = useRef(false);

  const beginInteraction = useCallback(() => {
    if (isDisabled || isInteractingRef.current) return;
    isInteractingRef.current = true;
    onInteractionStart?.();
  }, [isDisabled, onInteractionStart]);

  const endInteraction = useCallback(() => {
    if (!isInteractingRef.current) return;
    isInteractingRef.current = false;
    onInteractionEnd?.();
  }, [onInteractionEnd]);

  useEffect(
    () => () => {
      endInteraction();
    },
    [endInteraction],
  );

  const percentage =
    max === min ? 0 : Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));

  const handleChange = useCallback(
    (newValue: number) => {
      if (isDisabled) return;

      // Clamp value between min and max
      const clampedValue = Math.max(min, Math.min(max, newValue));

      // Round to nearest step
      const steppedValue = Math.round(clampedValue / step) * step;

      onChange(steppedValue);
    },
    [isDisabled, min, max, step, onChange],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseFloat(e.target.value);
      handleChange(newValue);
    },
    [handleChange],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDisabled) return;

      beginInteraction();
      setIsDragging(true);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const x = moveEvent.clientX - rect.left;
        const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
        const newValue = min + (percentage / 100) * (max - min);

        handleChange(newValue);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        endInteraction();
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      // Handle initial click
      handleMouseMove(e.nativeEvent);
    },
    [beginInteraction, endInteraction, isDisabled, min, max, handleChange],
  );

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} gap={12} width={width} fillWidth={fillWidth}>
      {label && (
        <FlexWrapper
          alignItems={AlignItems.CENTER}
          justifyContent={JustifyContent.SPACE_BETWEEN}
          fillWidth={fillWidth}
        >
          <InputLabel label={label} labelTooltip={labelTooltip} />
          <Text>{value}</Text>
        </FlexWrapper>
      )}
      <SliderWrapper ref={sliderRef} $isDisabled={isDisabled} onMouseDown={handleMouseDown}>
        <SliderTrack $isDisabled={isDisabled}>
          <SliderFill
            $percentage={percentage}
            $isDisabled={isDisabled}
            style={{ width: `${percentage}%` }}
          />
        </SliderTrack>
        <SliderHandle $percentage={percentage} $isDisabled={isDisabled} $isDragging={isDragging} />
        <HiddenInput
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleInputChange}
          onMouseDown={beginInteraction}
          onMouseUp={endInteraction}
          onTouchStart={beginInteraction}
          onTouchEnd={endInteraction}
          onKeyDown={beginInteraction}
          onKeyUp={endInteraction}
          onBlur={endInteraction}
          disabled={isDisabled}
          aria-label={label || "Slider"}
        />
      </SliderWrapper>
    </FlexWrapper>
  );
};

export default SliderInput;
